<template>
  <section class="">
    <div
      class="px-4 pt-6 mx-auto max-w-3xl lg:px-0 lg:pt-10"
      :class="paddingBottom === 'reduced' ? 'pb-6 lg:pb-6' : 'pb-12 lg:pb-16'"
    >
      <div class="flex flex-col gap-4">
        <div class="flex gap-3 items-center md:gap-5">
          <div class="relative shrink-0 size-9">
            <span
              class="absolute inset-0 rounded-full red-flag-badge-pulse bg-lifeboost-red"
              aria-hidden="true"
            />
            <div class="flex relative z-10 justify-center items-center text-base leading-7 text-white rounded-full size-9 bg-lifeboost-red font-ww-regular">
              {{ badgeNumber }}
            </div>
          </div>
          <span
            class="text-[11px] tracking-[1.43px] text-[#F1574B] uppercase font-ww-regular"
            v-html="badgeText"
          />
        </div>

        <h2
          class="text-2xl font-bold leading-tight sm:text-3xl lg:text-[34px] lg:leading-[42.5px] font-ww-bold text-[#3B2314]"
          v-html="title"
        />

        <div
          class="pt-1 text-[17px] leading-[25px] text-[#4A4A4A] font-ww-regular [&_p+p]:mt-5 [&_strong]:font-ww-bold"
          v-html="introContent"
        />

        <ul
          v-if="benefits.length"
          class="grid grid-cols-1 gap-2.5 pt-1 sm:grid-cols-2"
        >
          <li
            v-for="(benefit, index) in benefits"
            :key="index"
            class="flex gap-2.5 items-start px-[15px] py-[13px] min-h-[52px] rounded-md border border-[#E0D5C5] bg-[#FAF6F0]"
          >
            <span class="text-sm leading-[23.8px] text-[#F1574B] font-ww-regular shrink-0">✓</span>
            <span
              class="text-[15.5px] leading-[26.35px] text-[#4A4A4A] font-ww-regular"
              v-html="benefit.text"
            />
          </li>
        </ul>

        <div
          v-if="middleContent"
          class="text-[17px] leading-[25px] text-[#4A4A4A] font-ww-regular [&_p+p]:mt-5"
          v-html="middleContent"
        />

        <div
          v-if="quoteContent"
          class="pl-[26px] pr-[22px] pt-[26px] pb-[18px] w-full rounded-r-md border-l-4 border-[#2D5A27] bg-[#E8F0E6]"
        >
          <div
            class="text-[15px] leading-[25.5px] text-[#2D5A27] font-ww-regular [&_strong]:font-ww-bold [&_sup]:text-[11px]"
            v-html="quoteContent"
          />
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
defineProps({
  badgeNumber: {
    type: String,
    default: '4'
  },
  badgeText: {
    type: String,
    default: 'Red Flag No. 4'
  },
  title: {
    type: String,
    default: 'Why Losing Muscle Tanks Testosterone (And Everything Else)'
  },
  introContent: {
    type: String,
    default: `<p>Muscle mass is one of the strongest predictors of longevity.</p>
<p>But men naturally lose muscle as they age, which affects metabolism, testosterone, and sexual health.</p>
<p><strong>Research shows that:</strong></p>`
  },
  benefits: {
    type: Array,
    default: () => [
      { text: 'Resistance training increases testosterone levels' },
      { text: 'Creatine improves strength, muscle mass, and cognitive performance' },
      { text: 'Cardiovascular exercise improves erectile function' },
      { text: 'Stress reduction improves libido and hormone balance' }
    ]
  },
  middleContent: {
    type: String,
    default: `<p>And yes, clean coffee plays a role here too.</p>
<p>Caffeine can improve performance, enhance endurance, and support your metabolism.</p>`
  },
  quoteContent: {
    type: String,
    default: `<p>A meta-analysis in Sports Medicine found caffeine improves strength and power by <strong>6–12%</strong>. But, let's start with your muscles.<sup>22</sup></p>`
  },
  paddingBottom: {
    type: String,
    default: 'reduced',
    validator: (value) => ['default', 'reduced'].includes(value)
  }
})

defineBuilderComponent({
  friendlyName: 'Content - Red Flag Benefits Quote',
  inputs: [
    {
      name: 'badgeNumber',
      type: 'text',
      defaultValue: '4'
    },
    {
      name: 'badgeText',
      type: 'text',
      defaultValue: 'Red Flag No. 4'
    },
    {
      name: 'title',
      type: 'richText',
      defaultValue: 'Why Losing Muscle Tanks Testosterone (And Everything Else)'
    },
    {
      name: 'introContent',
      type: 'richText',
      defaultValue: `<p>Muscle mass is one of the strongest predictors of longevity.</p>
<p>But men naturally lose muscle as they age, which affects metabolism, testosterone, and sexual health.</p>
<p><strong>Research shows that:</strong></p>`
    },
    {
      name: 'benefits',
      type: 'list',
      defaultValue: [
        { text: 'Resistance training increases testosterone levels' },
        { text: 'Creatine improves strength, muscle mass, and cognitive performance' },
        { text: 'Cardiovascular exercise improves erectile function' },
        { text: 'Stress reduction improves libido and hormone balance' }
      ],
      subFields: [
        {
          name: 'text',
          type: 'text',
          defaultValue: ''
        }
      ]
    },
    {
      name: 'middleContent',
      type: 'richText',
      defaultValue: `<p>And yes, clean coffee plays a role here too.</p>
<p>Caffeine can improve performance, enhance endurance, and support your metabolism.</p>`
    },
    {
      name: 'quoteContent',
      type: 'richText',
      defaultValue: `<p>A meta-analysis in Sports Medicine found caffeine improves strength and power by <strong>6–12%</strong>. But, let's start with your muscles.<sup>22</sup></p>`
    },
    {
      name: 'paddingBottom',
      type: 'text',
      enum: ['default', 'reduced'],
      defaultValue: 'reduced'
    }
  ]
})
</script>
